import { useEffect, useRef, useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import Router from 'next/router';
import api from 'api';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import { useReduxState } from 'hooks';
import {
  decodeCustomAttributeKeyForForm,
  encodeCustomAttributeKeyForForm
} from 'lib/customAttributes';
import { Alert, Badge, Button, Link } from 'components/common';
import { Form, FormGroup, RadioGroup, Text } from 'components/common/form';
import { UserCustomAttribute } from 'components/user';

import type { Course, Path, UserCustomAttributes } from 'shared-types';

export interface RegisterFormProps {
  course?: Pick<
    Course,
    | 'id'
    | 'signUpLinkAllowAccountCreationType'
    | 'signUpLinkEnabled'
    | 'signUpLinkRestrictDomains'
    | 'slug'
  >;
  courseSignUpLinkHash?: string;
  next?: string;
  path?: Pick<
    Path,
    | 'id'
    | 'signUpLinkAllowAccountCreationType'
    | 'signUpLinkEnabled'
    | 'signUpLinkRestrictDomains'
  >;
  pathSignUpLinkHash?: string;
}

const RegisterForm = ({
  course,
  courseSignUpLinkHash,
  next,
  path,
  pathSignUpLinkHash
}: RegisterFormProps) => {
  const inputContainer = useRef<HTMLDivElement>(undefined);

  const {
    messages,
    serverConfig: {
      accountEnableUsernameAccounts,
      enableRegistrationType,
      registerRestrictDomains,
      userCustomAttributes
    },
    theme
  } = useReduxState();

  const [error, setError] = useState<React.ReactNode>(null);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [useUsername, setUseUsername] = useState<boolean>(false);

  useEffect(() => {
    inputContainer.current?.querySelector('input')?.focus();
  }, []);

  const handleUseUsernameChange = (value: boolean) => {
    setError(null);
    setUseUsername(value);
  };

  const handleSubmit = async (values) => {
    try {
      setError(null);
      setIsSubmitting(true);

      const data = {
        ...values,
        customAttributes: Object.fromEntries(
          Object.entries(values.customAttributes || {}).map(([key, value]) => [
            decodeCustomAttributeKeyForForm(key),
            value
          ])
        )
      };

      if (course && courseSignUpLinkHash) {
        data.courseId = course.id;
        data.courseSignUpLinkHash = courseSignUpLinkHash;
      }

      if (path && pathSignUpLinkHash) {
        data.pathId = path.id;
        data.pathSignUpLinkHash = pathSignUpLinkHash;
      }

      // used for redirecting user after registration
      if (next) data.next = next;

      const response = await api.post('/auth/local/register', data);

      if (response.data && response.data.success) {
        if (response.data.activationKey) {
          Router.push(
            `/create-account?key=${response.data.activationKey}` +
              `&username=${values.username}&customAttributes=${JSON.stringify(
                data.customAttributes
              )}` +
              (course && courseSignUpLinkHash
                ? `&next=/join/courses/${course.slug}/${courseSignUpLinkHash}`
                : '') +
              (!course && path && pathSignUpLinkHash
                ? `&next=/join/paths/${path.id}/${pathSignUpLinkHash}`
                : '')
          );
        } else {
          setIsSubmitting(false);
          setIsSuccess(true);
        }
      } else if (
        response.data?.error === 'invalid_custom_attribute_required_password'
      ) {
        setError(messages.authRequiredPasswordInvalid);
        setIsSubmitting(false);
      } else {
        setError(messages.commonError);
        setIsSubmitting(false);
      }
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setError(
          values.email ? (
            <div>
              <div>
                {`${messages.authEmailInUseError}. ${messages.authRegisterIfThisIsYourEmail}`}
              </div>
              <Link href="/reset-password" text={messages.authResetPassword} />
            </div>
          ) : (
            messages.authUsernameInUseError
          )
        );
        setIsSubmitting(false);
      } else {
        setError(messages.commonError);
        setIsSubmitting(false);
      }
    }
  };

  const validateEmail = (untrimmedValue: string) => {
    const value = untrimmedValue?.trim();
    if (!value || !isEmail(value, { domain_specific_validation: true })) {
      return messages.usersInvalidEmail;
    }

    let restrictDomains: string[] | undefined;
    if (course && courseSignUpLinkHash) {
      restrictDomains = course.signUpLinkRestrictDomains;
    } else if (path && pathSignUpLinkHash) {
      restrictDomains = path.signUpLinkRestrictDomains;
    } else {
      restrictDomains = registerRestrictDomains;
    }

    if (restrictDomains?.length) {
      const emailDomain = value.slice(value.lastIndexOf('@') + 1);
      if (!restrictDomains.includes(emailDomain)) {
        return messages.authRegisterDomainNotAllowed;
      }
    }
  };

  const validateUsername = (untrimmedValue: string) => {
    const value = untrimmedValue?.trim();
    if (!value) return messages.authInvalidUsername;
    if (isEmail(value)) return messages.authInvalidEmailAsUsername;
  };

  let allowUsernameAccounts = accountEnableUsernameAccounts;
  let allowEmailAccounts = true;
  if (courseSignUpLinkHash) {
    allowUsernameAccounts =
      allowUsernameAccounts &&
      ['any', 'username'].includes(
        course?.signUpLinkAllowAccountCreationType || 'any'
      ) &&
      !course?.signUpLinkRestrictDomains?.length;
    allowEmailAccounts =
      !allowUsernameAccounts ||
      ['any', 'email'].includes(
        course?.signUpLinkAllowAccountCreationType || 'any'
      );
  } else if (pathSignUpLinkHash) {
    allowUsernameAccounts =
      allowUsernameAccounts &&
      ['any', 'username'].includes(
        path?.signUpLinkAllowAccountCreationType || 'any'
      ) &&
      !path?.signUpLinkRestrictDomains?.length;
    allowEmailAccounts =
      !allowUsernameAccounts ||
      ['any', 'email'].includes(
        path?.signUpLinkAllowAccountCreationType || 'any'
      );
  } else {
    allowUsernameAccounts =
      allowUsernameAccounts &&
      ['any', 'username'].includes(enableRegistrationType);
    allowEmailAccounts =
      !allowUsernameAccounts ||
      ['any', 'email'].includes(enableRegistrationType);
  }

  return (
    <div className="register-form">
      {isSuccess ? (
        <Alert
          className="register-success"
          text={messages.authRegisterSuccess}
          type="success-light"
        />
      ) : (
        <>
          <Form onSubmit={handleSubmit}>
            {({ formState }) => (
              <div>
                {allowUsernameAccounts && allowEmailAccounts ? (
                  <RadioGroup
                    field="registerWithUsername"
                    id="register-with-username"
                    initialValue={useUsername}
                    onChange={handleUseUsernameChange}
                    options={[
                      {
                        id: 'register-with-username-0',
                        label: messages.authRegisterWithEmail,
                        value: false
                      },
                      {
                        id: 'register-with-username-1',
                        label: messages.authRegisterWithUsername,
                        value: true
                      }
                    ]}
                    style={{ marginBottom: 20 }}
                  />
                ) : null}

                {!useUsername && allowEmailAccounts ? (
                  <>
                    <div className="info">{messages.authRegisterInfo}</div>
                    <FormGroup htmlFor="email" label={messages.commonEmail}>
                      <Text
                        field="email"
                        id="email"
                        inputContainerRef={(el) => {
                          inputContainer.current = el;
                        }}
                        type="text"
                        validate={validateEmail}
                        validateOnChange={hasSubmitted}
                      />
                      {!formState.errors?.email &&
                      formState.values?.email?.match(/(Å|å|Ä|ä|Ö|ö|Ü|ü)/gm) ? (
                        <Badge
                          category="warning-light"
                          icon="exclamation-triangle"
                          style={{ marginTop: 10 }}
                          text={
                            messages.emailAddressesContainsSpecialCharacters
                          }
                        />
                      ) : null}
                    </FormGroup>
                  </>
                ) : (
                  <FormGroup htmlFor="username" label={messages.commonUsername}>
                    <Text
                      field="username"
                      id="username"
                      inputContainerRef={(el) => {
                        inputContainer.current = el;
                      }}
                      type="text"
                      validate={validateUsername}
                      validateOnChange={hasSubmitted}
                    />
                  </FormGroup>
                )}

                {userCustomAttributes
                  .filter((attribute) => attribute.showBeforeRegistration)
                  .map((attribute, i) => {
                    const customAttributes = Object.fromEntries(
                      Object.entries(
                        formState.values?.customAttributes || {}
                      ).map(([key, value]) => [
                        decodeCustomAttributeKeyForForm(key),
                        value
                      ])
                    ) as UserCustomAttributes;

                    const conditionsMatch = attributesConditionsMatch({
                      conditions: attribute.conditions,
                      customAttributeValues: customAttributes,
                      user: {
                        email: formState.values?.email,
                        isSelfRegistered: true
                      }
                    });
                    if (!conditionsMatch) return null;

                    const isRequired = attribute.isRequired?.value
                      ? attributesConditionsMatch({
                          conditions: attribute.isRequired?.conditions || [],
                          customAttributeValues: customAttributes,
                          isRequiredCheck: true,
                          user: {
                            email: formState.values?.email?.trim(),
                            isSelfRegistered: true
                          }
                        })
                      : false;

                    const error =
                      formState.errors[
                        encodeCustomAttributeKeyForForm(attribute.key)
                      ];

                    return (
                      <UserCustomAttribute
                        attribute={attribute}
                        calledFrom="registerForm"
                        customAttributeOriginalValues={{}}
                        customAttributeValues={{
                          ...formState.values?.customAttributes
                        }}
                        error={error}
                        id={i}
                        isAttributeRequired={isRequired}
                        isReadOnly={false}
                        key={`${attribute.key}-${i}-${isRequired}`}
                      />
                    );
                  })}

                {error ? (
                  <Alert
                    style={{ marginTop: 20 }}
                    text={error}
                    type="error-light"
                  />
                ) : null}

                <Button
                  category="success"
                  disabled={
                    !formState.values.email && !formState.values.username
                  }
                  loading={isSubmitting}
                  onClick={() => setHasSubmitted(true)}
                  text={messages.commonSend}
                  type="submit"
                  style={{ marginTop: 20, width: '100%' }}
                />
              </div>
            )}
          </Form>
        </>
      )}

      <style jsx>{`
        .info {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 5px;
        }
      `}</style>
    </div>
  );
};

export default RegisterForm;
